import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, Clock, Warning, Package, Truck, CheckCircle, Phone, ArrowsClockwise, DownloadSimple, ArrowSquareOut, PaperPlaneRight, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received: { label: "Received", color: "text-zinc-600 bg-zinc-100 border-zinc-200", icon: Clock },
  quoted: { label: "Quoted", color: "text-cyan-700 bg-cyan-50 border-cyan-200", icon: Clock },
  approved: { label: "Approved", color: "text-green-700 bg-green-50 border-green-200", icon: CheckCircle },
  info_needed: { label: "More Info Needed", color: "text-amber-700 bg-amber-50 border-amber-200", icon: Warning },
  compounding: { label: "Compounding", color: "text-blue-700 bg-blue-50 border-blue-200", icon: Package },
  shipped: { label: "Shipped", color: "text-violet-700 bg-violet-50 border-violet-200", icon: Truck },
  delivered: { label: "Delivered", color: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle },
};

const STATUS_ORDER = ["received", "quoted", "approved", "compounding", "shipped", "delivered"];

const DELIVERY_LABELS = {
  express_post: "Express Post ($12)",
  own_courier: "Own Courier / Driver (Free)",
  urgent_express: "URGENT + Express Post ($32)",
  urgent_courier: "URGENT + Own Courier ($20)",
  pickup: "Pickup from Amcal Newmarket (Free)",
};

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [viewingImage, setViewingImage] = useState(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [responding, setResponding] = useState(false);

  const respondToQuote = async (action, reason = "") => {
    setResponding(true);
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/respond-quote`, { action, reason }, { withCredentials: true });
      setOrder(data.order);
      setDeclineOpen(false);
      setDeclineReason("");
      toast.success(action === "approve" ? "Quote approved — we'll start compounding." : "Quote declined. We'll be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit response");
    }
    setResponding(false);
  };

  const viewScript = async (fileIds) => {
    if (!fileIds?.length) return;
    try {
      const res = await axios.get(`${API}/files/${fileIds[0]}`, { withCredentials: true, responseType: "blob" });
      setViewingImage(URL.createObjectURL(res.data));
    } catch { toast.error("Failed to load script"); }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
        setOrder(data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    const fetchMessages = async () => {
      try { const { data } = await axios.get(`${API}/orders/${orderId}/messages`, { withCredentials: true }); setMessages(data.messages); } catch { /* */ }
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [orderId]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${API}/orders/${orderId}/messages`, { message: newMessage }, { withCredentials: true });
      setNewMessage("");
      const { data } = await axios.get(`${API}/orders/${orderId}/messages`, { withCredentials: true });
      setMessages(data.messages);
    } catch { toast.error("Failed to send message"); }
  };

  if (loading) return <div className="p-12 text-center font-mono text-sm text-zinc-500">LOADING...</div>;
  if (!order) return <div className="p-12 text-center text-sm text-zinc-500">Order not found</div>;

  const currentIndex = STATUS_ORDER.indexOf(order.status);

  const handleReorder = async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/reorder`, { withCredentials: true });
      navigate("/orders/new", { state: { reorderData: data } });
    } catch { toast.error("Failed to prepare reorder"); }
  };

  const handleDownloadInvoice = () => {
    axios.get(`${API}/orders/${orderId}/invoice`, { withCredentials: true, responseType: "blob" }).then(res => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${orderId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Failed to download invoice"));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="order-detail-page">
      <Link to="/orders" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 transition-colors mb-6" data-testid="back-to-orders">
        <ArrowLeft size={16} /> Back to Orders
      </Link>

      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ORDER #{order.id?.slice(0, 8)}</p>
        <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Order Detail</h1>
      </div>

      {/* Status Timeline */}
      <div className="border border-zinc-200 p-6 mb-6" data-testid="status-timeline">
        <div className="flex items-center justify-between mb-4">
          {STATUS_ORDER.map((s, i) => {
            const sc = STATUS_CONFIG[s];
            const Icon = sc.icon;
            const isActive = i <= currentIndex;
            const isCurrent = s === order.status;
            return (
              <div key={s} className="flex flex-col items-center flex-1">
                <div className={`w-10 h-10 flex items-center justify-center border-2 transition-colors ${
                  isCurrent ? "border-[#0055FF] bg-[#0055FF] text-white" :
                  isActive ? "border-emerald-500 bg-emerald-50 text-emerald-700" :
                  "border-zinc-200 bg-white text-zinc-300"
                }`}>
                  <Icon size={20} weight={isCurrent ? "fill" : "regular"} />
                </div>
                <p className={`text-[10px] font-mono uppercase tracking-wider mt-2 text-center ${
                  isCurrent ? "text-[#0055FF] font-bold" : isActive ? "text-emerald-600" : "text-zinc-400"
                }`}>{sc.label}</p>
                {i < STATUS_ORDER.length - 1 && (
                  <div className={`hidden sm:block absolute w-full h-0.5 top-5 left-1/2 ${isActive ? "bg-emerald-400" : "bg-zinc-200"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tracking Number */}
      {order.tracking_number && (
        <div className="border border-violet-200 bg-violet-50 p-4 mb-6" data-testid="tracking-info">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-violet-500">TRACKING NUMBER</p>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-lg font-mono font-bold text-violet-900">{order.tracking_number}</p>
            <a
              href={`https://auspost.com.au/mypost/track/#/details/${order.tracking_number}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors"
              data-testid="track-auspost-link"
            >
              Track on AusPost <ArrowSquareOut size={14} />
            </a>
          </div>
        </div>
      )}

      {/* ETA */}
      {order.eta?.message && order.status !== "delivered" && (
        <div className="border border-blue-200 bg-blue-50 p-4 mb-6" data-testid="order-eta">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-blue-500">TIMELINE</p>
          <p className="text-sm font-semibold text-blue-900 mt-1">{order.eta.message}</p>
          {order.eta.date && <p className="text-xs text-blue-500 mt-1">Estimated ready: {order.eta.date}</p>}
        </div>
      )}

      {/* Quote Approval CTA (order owner only) */}
      {order.status === "quoted" && !order.has_quote_items && user?.role !== "admin" && (
        <div className="border-2 border-[#0055FF] bg-blue-50 p-5 mb-6" data-testid="quote-approval-banner">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#0055FF]">QUOTE READY</p>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-2">
            <div>
              <p className="text-sm text-zinc-700">Your quote has been priced. Approve to start compounding.</p>
              <p className="font-[Chivo] text-2xl font-black text-zinc-950 mt-1">${order.total?.toFixed(2)}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => respondToQuote("approve")}
                disabled={responding}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors disabled:opacity-50 active:scale-[0.98]"
                data-testid="approve-quote-button"
              >
                <ThumbsUp size={16} weight="fill" /> Approve Quote
              </button>
              <button
                onClick={() => setDeclineOpen(true)}
                disabled={responding}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-100 transition-colors disabled:opacity-50"
                data-testid="decline-quote-button"
              >
                <ThumbsDown size={16} /> Decline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="order-actions">
        <button
          onClick={handleReorder}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors"
          data-testid="reorder-button"
        >
          <ArrowsClockwise size={16} /> Reorder
        </button>
        <button
          onClick={handleDownloadInvoice}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 transition-colors"
          data-testid="download-invoice-button"
        >
          <DownloadSimple size={16} /> Download Invoice
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Delivery Info */}
        <div className="border border-zinc-200 p-4" data-testid="delivery-info">
          <h3 className="font-[Chivo] font-bold text-zinc-950 mb-3">Delivery</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-zinc-500">Method:</span> <span className="text-zinc-950 font-medium">{DELIVERY_LABELS[order.delivery_method] || order.delivery_method}</span></div>
            <div><span className="text-zinc-500">Fee:</span> <span className="text-zinc-950">${order.delivery_fee?.toFixed(2)}</span></div>
          </div>
        </div>

        {/* Order Info */}
        <div className="border border-zinc-200 p-4" data-testid="order-info">
          <h3 className="font-[Chivo] font-bold text-zinc-950 mb-3">Order Info</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-zinc-500">Placed by:</span> <span className="text-zinc-950 font-medium">{order.user_name}</span></div>
            {order.store_name && <div><span className="text-zinc-500">Store:</span> <span className="text-zinc-950">{order.store_name}</span></div>}
            {order.patient_name && <div><span className="text-zinc-500">Patient:</span> <span className="text-zinc-950">{order.patient_name}</span></div>}
            <div><span className="text-zinc-500">Prescriptions:</span> <span className="text-zinc-950">{order.prescription_file_ids?.length || 0} file(s)</span>
              {order.prescription_file_ids?.length > 0 && (
                <button onClick={() => viewScript(order.prescription_file_ids)} className="ml-2 px-2 py-0.5 text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors" data-testid="view-script-detail">View Script</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="border border-zinc-200 mb-6" data-testid="order-items">
        <div className="p-4 border-b border-zinc-200 bg-zinc-50">
          <h3 className="font-[Chivo] font-bold text-zinc-950">Order Items ({order.items?.length})</h3>
        </div>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="border-b border-zinc-200 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Ingredient</th>
              <th className="border-b border-zinc-200 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Strength</th>
              <th className="border-b border-zinc-200 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Qty</th>
              <th className="border-b border-zinc-200 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500 text-right">Price</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="py-3 px-4 text-sm text-zinc-800 font-medium">{item.ingredient}</td>
                <td className="py-3 px-4 text-sm text-zinc-600 font-mono">{item.strength}</td>
                <td className="py-3 px-4 text-sm text-zinc-600">{item.qty}</td>
                <td className="py-3 px-4 text-sm text-right font-mono font-semibold">
                  {item.quote_required ? (
                    <span className="text-amber-600 flex items-center gap-1 justify-end"><Phone size={12} />Quote Pending</span>
                  ) : `$${item.price?.toFixed(2)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-4 bg-zinc-50 border-t border-zinc-200">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-zinc-500">Subtotal</span>
            <span className="font-mono">${(order.total - order.delivery_fee).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-zinc-500">Delivery</span>
            <span className="font-mono">${order.delivery_fee?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-2 border-t border-zinc-200">
            <span>Total</span>
            <span className="font-mono">${order.total?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="border border-zinc-200 p-4 mb-6" data-testid="order-notes">
          <h3 className="font-[Chivo] font-bold text-zinc-950 mb-2">Notes</h3>
          <p className="text-sm text-zinc-800">{order.notes}</p>
        </div>
      )}

      {/* Status History */}
      <div className="border border-zinc-200" data-testid="status-history">
        <div className="p-4 border-b border-zinc-200 bg-zinc-50">
          <h3 className="font-[Chivo] font-bold text-zinc-950">Status History</h3>
        </div>
        <div className="divide-y divide-zinc-100">
          {order.status_history?.map((entry, i) => {
            const sc = STATUS_CONFIG[entry.status] || STATUS_CONFIG.received;
            return (
              <div key={i} className="flex items-start gap-3 p-4">
                <div className={`px-2 py-0.5 text-xs font-mono font-medium border ${sc.color} flex-shrink-0`}>{sc.label}</div>
                <div className="flex-1">
                  {entry.note && <p className="text-sm text-zinc-800">{entry.note}</p>}
                  <p className="text-xs text-zinc-400 mt-0.5">{new Date(entry.timestamp).toLocaleString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Decline Quote Dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-[Chivo]">Decline Quote</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">Let us know why you're declining (optional). We'll review and follow up.</p>
          <Textarea
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
            rows={3}
            className="rounded-none"
            placeholder="e.g. price too high, no longer needed, wrong item..."
            data-testid="decline-reason-input"
          />
          <DialogFooter>
            <button onClick={() => setDeclineOpen(false)} className="px-4 py-2 text-sm font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 transition-colors" data-testid="decline-cancel-button">Cancel</button>
            <button onClick={() => respondToQuote("decline", declineReason)} disabled={responding} className="px-4 py-2 text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50" data-testid="decline-confirm-button">
              {responding ? "Submitting..." : "Confirm Decline"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Script Image Viewer */}
      {viewingImage && (
        <Dialog open={!!viewingImage} onOpenChange={(open) => { if (!open) { URL.revokeObjectURL(viewingImage); setViewingImage(null); } }}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] p-2">
            <DialogHeader><DialogTitle className="font-[Chivo] px-2 pt-2">Prescription Script</DialogTitle></DialogHeader>
            <img src={viewingImage} alt="Prescription" className="max-w-full max-h-[75vh] object-contain" />
          </DialogContent>
        </Dialog>
      )}

      {/* Communication Thread */}
      <div className="border border-zinc-200 mt-6" data-testid="communication-thread">
        <div className="p-4 border-b border-zinc-200 bg-zinc-50">
          <h3 className="font-[Chivo] font-bold text-zinc-950">Messages</h3>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {messages.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">No messages yet. Start a conversation about this order.</div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {messages.map(msg => (
                <div key={msg.id} className={`p-3 ${msg.user_role === "admin" ? "bg-blue-50/30" : ""}`} data-testid={`message-${msg.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-zinc-800">{msg.user_name}</span>
                    <span className="text-[10px] font-mono uppercase text-zinc-400">{msg.user_role?.replace("_", " ")}</span>
                    <span className="text-xs text-zinc-400 ml-auto">{new Date(msg.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-zinc-700">{msg.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-3 border-t border-zinc-200 flex gap-2">
          <Input
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Type a message..."
            className="rounded-none flex-1"
            data-testid="message-input"
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim()}
            className="bg-[#0055FF] text-white px-4 py-2 hover:bg-[#0044CC] transition-colors disabled:opacity-50"
            data-testid="send-message-button"
          >
            <PaperPlaneRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
